// =============================================================================
// rememberFamilyFact — voice's family-memory write. Issue #888 step 3.
//
// ── Why this calls an RPC and not .from("family_context").insert() ───────────
//
// Voice tool handlers run in the BROWSER. kin-voice-session mints an OpenAI
// ephemeral token and exits, so no Kin server is in the live session (#725) and
// the write has to happen client-side. A plain client insert would bypass
// kin-chat's upsertFamilyFact and with it the #747 multi-valued key handling:
// a kid can legitimately have TWO allergies, and a single-value overwrite
// silently drops the first. That is safety-adjacent for `allergy` specifically,
// not a UX wart.
//
// Duplicating that rule in TypeScript for the browser would put the same safety
// logic in two places, which is how the two copies drift. public.upsert_family_fact
// is the one implementation both callers reach — see the migration's header.
//
// ── Why there is no userId parameter ─────────────────────────────────────────
//
// Unlike captureReminder(userId, ...), this takes none. The RPC is SECURITY
// INVOKER and resolves the writer from auth.uid(), so the account being written
// to is the session's, by construction, and cannot be redirected by an argument.
// Passing a userId here would be decorative at best and misleading at worst.
//
// ── Why a failed call is RE-READ before it is reported (#1857) ───────────────
//
// House rule 16 says never confirm a write you have not proved landed. The same
// rule points the other way too: a "didn't save" is as much a claim about the
// database as a "Saved!", and this helper used to make it on evidence that could
// not support it. A fetch that rejects — `status === 0` from PostgREST, or an
// outright throw — covers two different states:
//
//   1. the request never reached Postgres: nothing was written, `ok: false` is true;
//   2. the request COMMITTED and the response was lost (dropped connection,
//      timeout after the write, a gateway hiccup): the row holds the new value,
//      and `ok: false` is false. The Settings toggle then rolled back to the old
//      value and said "That didn't save." while the 7am text said *he*.
//
// Nothing in the error distinguishes them, so the helper asks the table. The
// read-back is safe: it is a plain SELECT on family_context, idempotent, with no
// side effect — unlike #1856's single-use Pinterest exchange code, where
// re-asking was actively unsafe and needed a different endpoint. Do not copy a
// retry pattern from there into here, and do not copy this re-read into there.
//
// ⚠️ Three outcomes, never two. Read-back finds the value → success. Read-back
// says it is absent → a real failure. Read-back ITSELF fails → `unverified`, a
// named third state. Folding that third one into either of the others is the
// original bug with a different sign: assuming the write landed puts a false
// "Saved!" over a lost write, which is the worse of the two lies.
//
// ⚠️ A DB-coded error (42501, 23514, PGRST…) is NOT re-read. Postgres answered,
// so nothing committed, and the read-back would only add a round trip.
// =============================================================================

import { supabase } from "@/integrations/supabase/client";
import {
  isDuplicateProposeError,
  isVoiceRateLimitError,
  VOICE_WRITE_ALREADY_SAVED_RESULT,
  VOICE_WRITE_RATE_LIMITED_RESULT,
} from "@/lib/voiceWriteBudget";
import { reportError } from "@/lib/reportError";
import { TRANSPORT_STATUS } from "@/lib/createConversationWithRetry";

/**
 * What the RPC did.
 *
 * `noop` — the fact was already recorded, verbatim.
 * `duplicate` — THIS PROPOSAL was already honored (#933). Different from noop:
 *   noop compares the value, duplicate compares the propose id, so a repeat
 *   carrying a *changed* value is still a duplicate and still writes nothing.
 * `recovered` — #1857. The response was lost but the read-back found the row
 *   holding this value. A SUCCESS (`ok: true`); named so logs can tell it from
 *   a clean insert, since the helper cannot know whether it was an insert, an
 *   update or a noop underneath.
 * `unverified` — #1857. The response was lost AND the read-back could not
 *   answer. `ok: false`, but NOT a failure: the row may or may not hold the
 *   value. Callers that show her anything must say "couldn't confirm", never
 *   "didn't save", and this is the one outcome where a retry is informative.
 */
export type FamilyFactAction =
  | "insert"
  | "update"
  | "noop"
  | "duplicate"
  | "recovered"
  | "unverified"
  | "rejected_category"
  | "rejected_empty"
  | "rejected_medical"
  | "rejected_rate_limit";

export interface RememberFamilyFactInput {
  category: string;
  subject?: string | null;
  key: string;
  value: string;
  /**
   * #933. Correlation id from the propose→capture pair. When present, a second
   * call carrying the same id returns the first row instead of writing another.
   */
  proposeId?: string | null;
}

export interface RememberFamilyFactResult {
  ok: boolean;
  action?: FamilyFactAction;
  /** Present when a row was written or matched. Undo uses it. */
  factId?: string;
  /** Model-facing, already phrased for Kin to speak. */
  error?: string;
}

/**
 * Rejections that are the CALLER's fault and must never be retried, mapped to
 * what Kin should say. Kept as prose rather than codes because the string goes
 * straight back to the realtime model as a tool result and is about to be
 * spoken — an error code would get read aloud.
 *
 * ⚠️ `rejected_medical` is deliberately not apologetic-and-vague. She asked Kin
 * to remember a dosage; the honest answer is that Kin does not keep those, not
 * "something went wrong."
 */
const REJECTION_MESSAGES: Partial<Record<FamilyFactAction, string>> = {
  rejected_category:
    "That doesn't fit any of the things you can remember. Don't retry — just move on.",
  rejected_empty: "That fact came through empty. Don't retry.",
  rejected_medical:
    "You don't keep medication dosages or diagnoses. Tell her that warmly, in your own words, and don't retry.",
  // #933. Not the caller's fault and not permanent, but still a don't-retry:
  // retrying is the loop the cap exists to break.
  rejected_rate_limit: VOICE_WRITE_RATE_LIMITED_RESULT,
};

/**
 * The write did not land — Postgres said so, or the read-back proved the row
 * absent. Model-facing, like the rejections above.
 *
 * ⚠️ #1857 removed "and don't retry" from this one. That advice is right for
 * the rejections (retrying cannot help) and wrong here: a transient failure
 * is exactly the case where trying once more may work, and telling her not to
 * was what kept a false "didn't save" from ever being discovered.
 */
export const FAMILY_FACT_FAILED_RESULT = "Couldn't save that. Tell her honestly.";

/**
 * #1857 — the third state. The response was lost and the read-back could not
 * answer, so the fact may or may not be saved. Says so, and says a retry is
 * fine: the retry is the one action that would show her the truth.
 */
export const FAMILY_FACT_UNVERIFIED_RESULT =
  "Couldn't confirm that saved. The connection dropped mid-save, so it may have. Tell her honestly, and it's fine to try once more.";

/**
 * upsert_family_fact stores `left(btrim(value), 500)`. The read-back compares
 * against what the RPC would have stored, not what the caller sent.
 */
const STORED_VALUE_MAX = 500;

export type FamilyFactReadBack =
  | { state: "landed"; factId?: string }
  | { state: "absent" }
  | { state: "unknown" };

/**
 * #1857 — asks the table whether a (category, subject, key) row holds `value`.
 *
 * Idempotent and side-effect free: a plain SELECT, matching the RPC's own
 * lookup (`subject is not distinct from`, `archived = false`). Scoped by RLS to
 * the session's account, the same way the RPC scopes its write by auth.uid() —
 * no user id here for the same reason there is none on the write.
 *
 * `unknown` is a real answer, not a default: it is returned ONLY when the read
 * itself failed. A read that succeeded and found no matching row is `absent`.
 */
export async function readBackFamilyFact(
  category: string,
  subject: string | null,
  key: string,
  value: string,
): Promise<FamilyFactReadBack> {
  try {
    let query = (supabase as any)
      .from("family_context")
      .select("id, value")
      .eq("category", category)
      .eq("key", key)
      .eq("archived", false);
    query = subject === null ? query.is("subject", null) : query.eq("subject", subject);
    const { data, error } = await query;
    if (error || !Array.isArray(data)) {
      console.warn("[family-memory] read-back could not answer:", error ?? data);
      return { state: "unknown" };
    }
    const stored = value.slice(0, STORED_VALUE_MAX);
    const hit = data.find((r: any) => String(r?.value ?? "") === stored);
    return hit ? { state: "landed", factId: hit.id ?? undefined } : { state: "absent" };
  } catch (e) {
    console.warn("[family-memory] read-back threw:", e instanceof Error ? e.message : String(e));
    return { state: "unknown" };
  }
}

/**
 * #1857 — what to report when the RPC's answer never arrived.
 *
 * Reached from exactly two places: a PostgREST `status === 0` (fetch rejected,
 * the shape measured in createConversationWithRetry.ts) and the outer catch.
 * Never from a DB-coded error — Postgres answered those, so nothing committed.
 */
async function settleLostResponse(
  category: string,
  subject: string | null,
  key: string,
  value: string,
  cause: unknown,
): Promise<RememberFamilyFactResult> {
  const back = await readBackFamilyFact(category, subject, key, value);
  if (back.state === "landed") {
    // Not an error: the write is there. Warn-level so Sentry does not file a
    // "save failed" over a saved fact — that false report is the #1857 symptom.
    console.warn("[family-memory] response lost but the row holds the value — reporting success:", cause);
    return { ok: true, action: "recovered", factId: back.factId };
  }
  if (back.state === "absent") {
    console.error("[family-memory] rpc failed and the row is absent:", cause);
    return { ok: false, error: FAMILY_FACT_FAILED_RESULT };
  }
  console.error("[family-memory] rpc failed and the read-back could not answer:", cause);
  return { ok: false, action: "unverified", error: FAMILY_FACT_UNVERIFIED_RESULT };
}

/**
 * True for the #1857 third state, whether you are holding the result object or
 * the Error a caller built from it (`code: result.action`). Callers branch on
 * this to say "couldn't confirm" instead of "didn't save".
 */
export function isUnverifiedFactWrite(x: unknown): boolean {
  if (typeof x !== "object" || x === null) return false;
  const { action, code } = x as { action?: unknown; code?: unknown };
  return action === "unverified" || code === "unverified";
}

/**
 * Saves one durable family fact for the signed-in user.
 *
 * Never throws: every failure path returns a result the caller can hand to the
 * model as a normal tool result. A thrown error inside a voice tool handler
 * leaves the model waiting on a result that never arrives, and Kin goes silent
 * mid-call — the same failure shape SEARCH_CLIENT_TIMEOUT_MS exists to prevent.
 */
export async function rememberFamilyFact(
  fact: RememberFamilyFactInput,
  source = "voice",
  confidence = "high",
): Promise<RememberFamilyFactResult> {
  const category = String(fact?.category ?? "").trim();
  const key = String(fact?.key ?? "").trim();
  const value = String(fact?.value ?? "").trim();
  const rawSubject = fact?.subject == null ? "" : String(fact.subject).trim();
  const subject = rawSubject === "" ? null : rawSubject;
  const proposeId = fact?.proposeId ? String(fact.proposeId) : null;

  if (!category || !key || !value) {
    return { ok: false, error: "That fact came through empty. Don't retry." };
  }

  try {
    // Cast to `any`: src/integrations/supabase/types.ts is regenerated from the
    // LIVE schema and cannot know about upsert_family_fact until Kasandra runs
    // the migration — the same generated-types lag every new-column PR in this
    // repo hits (see captureReminder.ts). Remove once types.ts is regenerated.
    const baseArgs = {
      p_category: category,
      p_subject: subject,
      p_key: key,
      p_value: value,
      p_source: source,
      p_confidence: confidence,
    };

    let { data, error, status } = await (supabase as any).rpc(
      "upsert_family_fact",
      proposeId ? { ...baseArgs, p_propose_id: proposeId } : baseArgs,
    );

    // ⚠️ Deploy-order fallback (#933). The frontend ships on merge; Kasandra
    // runs the SQL by hand, and the gap between those is real. Until the
    // migration runs, `upsert_family_fact` takes six arguments and PostgREST
    // answers a seven-argument call with PGRST202 "could not find the
    // function". Without this retry, every voice fact write would fail for the
    // whole window — a shipped outage waiting on a human. Same reasoning as
    // kin-chat's own fallback for this function.
    //
    // Costs one wasted round trip, once per call, only while the schema is
    // behind. Delete it when types.ts is regenerated post-migration.
    if (error && proposeId && String((error as any)?.code ?? "") === "PGRST202") {
      console.warn("[family-memory] upsert_family_fact has no p_propose_id yet — migration not run; retrying without it");
      ({ data, error, status } = await (supabase as any).rpc("upsert_family_fact", baseArgs));
    }

    if (error) {
      // ⚠️ The race the unique index exists for. Two truly simultaneous calls
      // can both pass the propose lookup inside the function; one then loses on
      // the index. That is a SUCCESS — the other call wrote the fact — and
      // reporting it as a failure would have Kin apologise for having already
      // saved something, and invite a retry.
      //
      // No factId here: the losing call never saw the winner's row. Undo is
      // unavailable for this one, which is honest — the alternative is guessing
      // at an id, and a wrong id means Undo archives the wrong row (#915).
      if (isDuplicateProposeError(error)) {
        return { ok: true, action: "duplicate" };
      }
      // A cap refusal can also arrive as a raised exception rather than a
      // returned action — the trigger is the floor beneath the RPC and fires
      // when something inserts around it. Recognise it before the generic
      // "couldn't save that", which would invite a retry.
      if (isVoiceRateLimitError(error)) {
        return { ok: false, action: "rejected_rate_limit", error: VOICE_WRITE_RATE_LIMITED_RESULT };
      }
      // #1857 — the fetch rejected: PostgREST sets status 0 in exactly that
      // catch and nowhere else. The write may have committed. Ask the table.
      if (status === TRANSPORT_STATUS) {
        return await settleLostResponse(category, subject, key, value, error);
      }
      console.error("[family-memory] rpc upsert_family_fact failed:", error);
      return { ok: false, error: FAMILY_FACT_FAILED_RESULT };
    }

    // `returns table (...)` comes back as an array of rows, even for one row.
    const row = Array.isArray(data) ? data[0] : data;
    const action = (row?.action ?? undefined) as FamilyFactAction | undefined;

    if (!action) {
      // A successful call that returned nothing means the function ran and
      // decided nothing — treat as a failure rather than reporting a save that
      // may not have happened. #537's fix created #545 exactly by assuming the
      // happy path when the write's outcome was never actually read.
      console.error("[family-memory] rpc returned no action row:", data);
      return { ok: false, error: FAMILY_FACT_FAILED_RESULT };
    }

    const rejection = REJECTION_MESSAGES[action];
    if (rejection) return { ok: false, action, error: rejection };

    return { ok: true, action, factId: row?.fact_id ?? undefined };
  } catch (e) {
    // #1857 — a throw is the other way a lost response arrives. Same question,
    // same three answers; never a bare "didn't save" from here.
    return await settleLostResponse(category, subject, key, value, e);
  }
}

/**
 * Undoes one saved fact BY ROW ID.
 *
 * ⚠️ Contrast the [LEARN] chip's Undo in ChatMessages.tsx, which matches on
 * category+key+subject because the tag never carried an id. For a multi-valued
 * key that matcher archives every sibling row: undoing a second allergy removes
 * the first one too. The RPC hands back the exact id, so voice does not inherit
 * that. Pointing the chat chip at ids too is follow-up work, not this diff.
 */
export async function undoFamilyFact(factId: string): Promise<boolean> {
  if (!factId) return false;
  try {
    // ⚠️ `.select("id")` is load-bearing, not a convenience. An RLS-filtered
    // UPDATE that matches ZERO rows returns 200 with `error === null` — reading
    // only the error cannot see it, and the caller would say "Okay, forgotten."
    // over a row that is still there. House rule 16: a confirmation is a claim
    // about the database, so ask for the rows back and assert they came.
    //
    // Caught by Copilot on #905. The toast this feeds already cited the rule;
    // the helper underneath it did not follow it.
    const { data, error } = await (supabase as any)
      .from("family_context")
      .update({ archived: true })
      .eq("id", factId)
      .select("id");
    if (error) {
      reportError("[family-memory] undo failed", error, { tags: { area: "family-memory" } });
      return false;
    }
    if (!Array.isArray(data) || data.length === 0) {
      reportError("[family-memory] undo matched no rows", factId, { tags: { area: "family-memory" } });
      return false;
    }
    return true;
  } catch (e) {
    console.warn("[family-memory] undo threw:", e instanceof Error ? e.message : String(e));
    return false;
  }
}
